import { useEffect, useRef, useState, type FormEvent } from "react";
import type { SiteSocialLink } from "@tih/shared";
import {
  ApiError,
  adminCreateSiteSocialLink,
  adminDeleteSiteSocialLink,
  adminFetchSiteSocialLinks,
  adminReorderSiteSocialLinks,
  adminUpdateSiteSocialLink,
} from "../../../api/client";

const EMPTY_FORM = {
  label: "",
  href: "",
  published: true,
};

export function AdminSettingsSocialPage() {
  const formRef = useRef<HTMLElement>(null);
  const [rows, setRows] = useState<SiteSocialLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });

  const showForm = creatingNew || editingId != null;

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setRows(await adminFetchSiteSocialLinks());
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function resetForm() {
    setCreatingNew(false);
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
  }

  function startCreate() {
    resetForm();
    setCreatingNew(true);
    setSuccess(null);
    setError(null);
    requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  function startEdit(row: SiteSocialLink) {
    setCreatingNew(false);
    setEditingId(row.id);
    setForm({
      label: row.label,
      href: row.href,
      published: row.published,
    });
    setSuccess(null);
    setError(null);
    requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSuccess(null);

    const label = form.label.trim();
    const href = form.href.trim();
    if (!label) {
      setError("Label is required");
      setBusy(false);
      return;
    }
    if (!href) {
      setError("Link URL is required");
      setBusy(false);
      return;
    }

    try {
      if (editingId != null) {
        await adminUpdateSiteSocialLink(editingId, {
          label,
          href,
          published: form.published,
        });
        setSuccess("Social link updated.");
      } else {
        await adminCreateSiteSocialLink({
          label,
          href,
          published: form.published,
        });
        setSuccess("Social link added.");
      }
      resetForm();
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save social link");
    } finally {
      setBusy(false);
    }
  }

  async function togglePublished(row: SiteSocialLink) {
    setBusy(true);
    setError(null);
    try {
      await adminUpdateSiteSocialLink(row.id, { published: !row.published });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not update social link");
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: number) {
    if (!confirm("Delete this social link permanently?")) return;
    setBusy(true);
    setError(null);
    try {
      await adminDeleteSiteSocialLink(id);
      if (editingId === id) resetForm();
      setSuccess("Social link deleted.");
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not delete social link");
    } finally {
      setBusy(false);
    }
  }

  async function move(id: number, direction: -1 | 1) {
    const index = rows.findIndex((row) => row.id === id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= rows.length) return;

    const nextIds = rows.map((row) => row.id);
    [nextIds[index], nextIds[target]] = [nextIds[target], nextIds[index]];

    setBusy(true);
    setError(null);
    try {
      setRows(await adminReorderSiteSocialLinks(nextIds));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not reorder social links");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return <div style={{ opacity: 0.5 }}>Loading…</div>;
  }

  return (
    <div>
      <section className="admin-card">
        <div className="admin-page-header-row" style={{ marginBottom: 16 }}>
          <div>
            <h2 className="admin-card-title" style={{ marginBottom: 8 }}>Footer social links</h2>
            <p className="admin-card-sub" style={{ margin: 0 }}>
              Social profiles shown in the fourth footer column. Reorder with the arrows —
              only published links appear on the public site.
            </p>
          </div>
          {!showForm && (
            <button type="button" className="btn btn-primary" onClick={startCreate}>
              + Add link
            </button>
          )}
        </div>

        {rows.length === 0 ? (
          <p style={{ opacity: 0.6, margin: 0 }}>No social links yet.</p>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Label</th>
                  <th>Link</th>
                  <th>Status</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row.id}>
                    <td>{row.label}</td>
                    <td>
                      <a href={row.href} target="_blank" rel="noopener noreferrer">
                        {row.href}
                      </a>
                    </td>
                    <td>{row.published ? "Published" : "Hidden"}</td>
                    <td className="admin-table-actions">
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        disabled={busy || index === 0}
                        onClick={() => void move(row.id, -1)}
                        aria-label="Move up"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        disabled={busy || index === rows.length - 1}
                        onClick={() => void move(row.id, 1)}
                        aria-label="Move down"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        disabled={busy}
                        onClick={() => startEdit(row)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        disabled={busy}
                        onClick={() => void togglePublished(row)}
                      >
                        {row.published ? "Hide" : "Publish"}
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        disabled={busy}
                        onClick={() => void remove(row.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {showForm && (
        <section ref={formRef} className="admin-card" style={{ marginTop: 24 }}>
          <h2 className="admin-card-title">
            {editingId != null ? "Edit social link" : "Add social link"}
          </h2>
          <form onSubmit={onSubmit} className="admin-blog-form">
            <div className="admin-blog-form-fields">
              <label>
                <span>Label (shown in footer)</span>
                <input
                  value={form.label}
                  onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                  placeholder="@together_heaven"
                  disabled={busy}
                  required
                />
              </label>
              <label>
                <span>Profile URL</span>
                <input
                  value={form.href}
                  onChange={(e) => setForm((f) => ({ ...f, href: e.target.value }))}
                  placeholder="https://instagram.com/togetherinheaven"
                  disabled={busy}
                  required
                />
              </label>
              <label>
                <span>Published on the public site</span>
                <input
                  type="checkbox"
                  checked={form.published}
                  onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))}
                  disabled={busy}
                />
              </label>
            </div>
            <div className="admin-blog-form-actions">
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {busy ? "Saving…" : editingId != null ? "Save changes" : "Add link"}
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={busy}
                onClick={resetForm}
              >
                Cancel
              </button>
            </div>
          </form>
          <p className="admin-card-foot">
            Use full profile URLs (<code>https://…</code>) for X, Facebook, Instagram, YouTube,
            TikTok, and other social platforms.
          </p>
        </section>
      )}

      {error && <div className="form-error">{error}</div>}
      {success && (
        <div className="form-success" style={{ padding: 12, marginTop: 16 }}>
          {success}
        </div>
      )}
    </div>
  );
}
